/**
 * M9 CH-4: a hulk's flaws and its First Rush pattern edge (plan `docs/plans/2026-10-05-m9-claimable-hulls.md` §4.2,
 * MS-2; economy direction §2.7 "flaws with an upside").
 *
 * - **The bitmask** (`OwnedShip.flaws`, `HulkSite.flaws`, the worker's Uint16 `FleetStore.flaws`): bits 0..11 are
 *   `hulks.json flaws[i]`, bits 12..15 the FR edge `frEdges.edges[i]` (at most one). The list is append-only:
 *   saved bitmasks name flaws by index.
 * - **The roll is a hash, never an RNG draw** (MS-2): `hash32(seed, hashString(hull), SALT, i)` over the hulk's own
 *   seed (CH-2a's `HulkSpot.seed`, or the off-screen wreck's seed), so no M4/M6 hash moves. 1..n flaws by
 *   `flawCountWeights`, each drawn without replacement by `weight`.
 * - **The FR edge** (D7(c)): a seeded First Rush hulk (`hk:` key) carries exactly one small stat edge, never
 *   tetherstone. An off-screen combat loss (`ow-` key) has none.
 * - **Effects:** each flaw scales one `ships.json` stat (`traitMults`, `applyHulkTraits` for CH-9a), the hull value
 *   by the product of the flaws' `valueMult` (CH-8), and costs `fix` goods at a yard (`flawFixCost`, CH-10).
 *
 * Pure; survey, yard and load time only, never per frame (it allocates).
 */
import { z } from 'zod'
import { GOOD_BASE_PRICE, isGoodId, type GoodId, type Goods } from '../career/parts'
import hullsJson from '../data/strategic/hulls.json'
import { hash32, hashString } from '../space/hash'
import { COMPONENT_WARE_IDS } from '../strategic/wares'
import { hulksConfig, MAX_FLAWS, type HulkEdge, type HulkFlaw, type HulksConfig, type HulkStat } from './data'

/** Salts for the flaw draws (fixed forever: changing one rerolls every hulk's flaws). */
const SALT_FLAW_COUNT = 0x464c4357 // 'FLCW'
const SALT_FLAW_PICK = 0x464c504b // 'FLPK'
const SALT_FR_EDGE = 0x46524547 // 'FREG'

/** The first edge bit: bits below are flaws. */
export const EDGE_SHIFT = MAX_FLAWS
/** The flaw bits of a bitmask. */
export const FLAW_MASK = (1 << EDGE_SHIFT) - 1
/** The edge bits of a bitmask. */
export const EDGE_MASK = 0xffff & ~FLAW_MASK

const U32 = 4294967296

/** The index drawn from `weights` by a uniform `u` in [0, 1), skipping `taken` indices; -1 when nothing is left. */
function pickWeighted(weights: readonly number[], u: number, taken = 0): number {
  let total = 0
  for (let i = 0; i < weights.length; i++) if ((taken & (1 << i)) === 0) total += weights[i]
  if (total <= 0) return -1
  let x = u * total
  let last = -1
  for (let i = 0; i < weights.length; i++) {
    if ((taken & (1 << i)) !== 0 || weights[i] <= 0) continue
    last = i
    if (x < weights[i]) return i
    x -= weights[i]
  }
  return last
}

/** A seeded First Rush hulk (CH-2a's `hk:<sys>:<n>` key) rather than an off-screen combat loss (`ow-<seq>`). */
export function isFirstRushKey(key: string): boolean {
  return key.startsWith('hk:')
}

/** The flaw bits of a hulk of `hull` with `seed` (1..n flaws, by hash; MS-2). */
export function rollFlaws(seed: number, hull: string, cfg: HulksConfig = hulksConfig()): number {
  const h = hashString(hull)
  const count = pickWeighted(cfg.flawCountWeights, hash32(seed, h, SALT_FLAW_COUNT) / U32) + 1
  const weights = cfg.flaws.map((f) => f.weight)
  let bits = 0
  for (let i = 0; i < count; i++) {
    const pick = pickWeighted(weights, hash32(seed, h, SALT_FLAW_PICK, i) / U32, bits)
    if (pick < 0) break
    bits |= 1 << pick
  }
  return bits
}

/** The FR edge bit of a seeded hulk with `seed` (one edge, by hash). */
export function rollEdge(seed: number, cfg: HulksConfig = hulksConfig()): number {
  const i = Math.floor((hash32(seed, SALT_FR_EDGE) / U32) * cfg.frEdges.edges.length)
  return 1 << (EDGE_SHIFT + i)
}

/** Everything the survey rolls for a hulk: its flaws, plus the FR edge when it is a seeded First Rush hulk. */
export function hulkTraits(key: string, seed: number, hull: string, cfg: HulksConfig = hulksConfig()): number {
  return rollFlaws(seed, hull, cfg) | (isFirstRushKey(key) ? rollEdge(seed, cfg) : 0)
}

/** The flaws a bitmask names, in list order (unknown bits are ignored). */
export function flawsOf(bits: number, cfg: HulksConfig = hulksConfig()): HulkFlaw[] {
  return cfg.flaws.filter((_, i) => (bits & (1 << i)) !== 0)
}

/** How many flaws a bitmask holds (the edge is not a flaw). */
export function flawCount(bits: number, cfg: HulksConfig = hulksConfig()): number {
  return flawsOf(bits, cfg).length
}

/** The FR edge a bitmask names, or null. */
export function edgeOf(bits: number, cfg: HulksConfig = hulksConfig()): HulkEdge | null {
  const edges = cfg.frEdges.edges
  for (let i = 0; i < edges.length; i++) if ((bits & (1 << (EDGE_SHIFT + i))) !== 0) return edges[i]
  return null
}

/** The bitmask without the flaw at list index `i` (a yard fix, CH-10); the edge stays. */
export function withoutFlaw(bits: number, i: number): number {
  return bits & ~(1 << i) & 0xffff
}

/** The hull value multiplier of a bitmask: the product of its flaws' `valueMult`, in (0, 1], to 3 places. */
export function valueMultOf(bits: number, cfg: HulksConfig = hulksConfig()): number {
  let m = 1
  for (const f of flawsOf(bits, cfg)) m *= f.valueMult
  return Math.max(0.001, Math.round(m * 1000) / 1000)
}

/** One stat multiplier (a flaw's or the edge's). */
export interface StatMult {
  readonly stat: HulkStat
  readonly mult: number
}

/** The stat multipliers of a bitmask: each flaw's, then the edge's (CH-9a applies them in this order). */
export function traitMults(bits: number, cfg: HulksConfig = hulksConfig()): StatMult[] {
  const out: StatMult[] = flawsOf(bits, cfg).map((f) => ({ stat: f.stat, mult: f.mult }))
  const edge = edgeOf(bits, cfg)
  if (edge !== null) out.push({ stat: edge.stat, mult: edge.mult })
  return out
}

/** A copy of `stats` with a bitmask's multipliers applied (CH-9a, on the flown ship's def). 0 → an equal copy. */
export function applyHulkTraits<T extends Record<HulkStat, number>>(stats: Readonly<T>, bits: number, cfg: HulksConfig = hulksConfig()): T {
  const out: Record<HulkStat, number> = { ...stats }
  for (const m of traitMults(bits, cfg)) out[m.stat] *= m.mult
  return { ...stats, ...out }
}

// ---------------------------------------------------------------------------------------------
// Hull values (strategic `hulls.json` buildCost at base price, as `strategic/data.ts hullValue`)
//
// Two values per hull (the kit rule, T52; addendum `docs/plans/2026-10-08-5e-post-m9-addendum.md` §3):
// - the **hull value** (`hullValueOf`): the full build cost at base price, the worker's `hullValue` mirror. Yard
//   flaw fixes (`flawFixCost`) and the insurance cap (`insure.ts`) read it.
// - the **claim cost** (`claimCostOf`, `claimValueOf`): only the component-tier part of the build cost
//   (`COMPONENT_WARE_IDS`: plating, electronics, drive and weapon parts). Hulk kits (`survey.ts hulkKit`) and scrap
//   (`scrap.ts scrapQuote`) read it, so a refined or import ware in a build cost (the planned alloy) never enters a
//   kit and never comes out of a scrap. Today every build cost is component-only, so the two are equal.
// ---------------------------------------------------------------------------------------------

const hullCostsSchema = z.object({
  hulls: z.array(z.object({ id: z.string(), buildCost: z.record(z.string(), z.number().nonnegative()) })),
})

const BUILD_COST: ReadonlyMap<string, Readonly<Goods>> = (() => {
  const out = new Map<string, Goods>()
  for (const h of hullCostsSchema.parse(hullsJson).hulls) {
    const goods: Goods = {}
    for (const [w, n] of Object.entries(h.buildCost)) {
      if (!isGoodId(w)) throw new Error(`hulks: hull ${h.id} buildCost names ${w}, not a good`)
      goods[w] = n
    }
    out.set(h.id, Object.freeze(goods))
  }
  return out
})()

/** A strategic hull's build cost (component goods), or null for an unknown hull. */
export function buildCostOf(hull: string): Readonly<Goods> | null {
  return BUILD_COST.get(hull) ?? null
}

/** Credits of a goods block at base price. */
export function goodsValue(goods: Readonly<Goods>): number {
  let v = 0
  for (const [g, n] of Object.entries(goods) as [GoodId, number | undefined][]) v += (n ?? 0) * GOOD_BASE_PRICE[g]
  return v
}

/**
 * A pristine hull's value: Σ buildCost × base price (the strategic `hullValue`, which the worker's insurance multiplies
 * by `valueMult`), 0 for an unknown hull. The full build cost, never the claim cost: yard fixes and the insurance cap.
 */
export function hullValueOf(hull: string): number {
  const cost = buildCostOf(hull)
  return cost === null ? 0 : goodsValue(cost)
}

const COMPONENT_GOODS: ReadonlySet<string> = new Set(COMPONENT_WARE_IDS)

/** The component-tier part of a build cost (`COMPONENT_WARE_IDS` only; every other good is dropped). */
export function claimCost(cost: Readonly<Goods>): Goods {
  const out: Goods = {}
  for (const [g, n] of Object.entries(cost) as [GoodId, number | undefined][]) {
    if (n !== undefined && COMPONENT_GOODS.has(g)) out[g] = n
  }
  return out
}

const CLAIM_COST: ReadonlyMap<string, Readonly<Goods>> = new Map(
  [...BUILD_COST].map(([id, cost]): [string, Readonly<Goods>] => [id, Object.freeze(claimCost(cost))]),
)

/** A hull's claim cost: its build cost's component-tier part (the kit rule, T52), or null for an unknown hull. */
export function claimCostOf(hull: string): Readonly<Goods> | null {
  return CLAIM_COST.get(hull) ?? null
}

/** A hull's claim value: its claim cost at base price (hulk kits and scrap credits), 0 for an unknown hull. */
export function claimValueOf(hull: string): number {
  const cost = claimCostOf(hull)
  return cost === null ? 0 : goodsValue(cost)
}

/**
 * The goods a yard charges to fix flaw `i` on a `hull` (CH-10): per good, ceil(share × hull value ÷ base price). The
 * full hull value (`hullValueOf`), not the claim value, so "3 fixes cost more than a new hull" holds (T52).
 */
export function flawFixCost(hull: string, i: number, cfg: HulksConfig = hulksConfig()): Goods {
  const flaw = cfg.flaws[i]
  const value = hullValueOf(hull)
  const out: Goods = {}
  if (flaw === undefined || value <= 0) return out
  for (const [g, s] of Object.entries(flaw.fix) as [GoodId, number | undefined][]) {
    if (s === undefined) continue
    out[g] = Math.ceil((s * value) / GOOD_BASE_PRICE[g] - 1e-9)
  }
  return out
}
